/**
 * Error field registry for emphasis forms using FormValidator.
 *
 * Maps input ids/names to human-readable labels and focus targets so
 * ErrorSummary can build a clickable list and focus management can
 * resolve the correct DOM element.
 *
 * Designed to work generically with any form that uses `data-validation`
 * attributes — not just the PESF. Call `buildRegistryFromForm()` with
 * the form element to discover all validated fields automatically.
 */

export interface ErrorFieldEntry {
  /** Human-readable field label shown in the ErrorSummary */
  label: string;
  /** DOM id of the input or focusable element */
  fieldId: string;
  /** Optional CSS selector when fieldId alone can't resolve focus target */
  selector?: string;
}

/** Shape consumed by ErrorSummary */
export interface StructuredError {
  fieldId: string;
  fieldName: string;
  message: string;
}

/** Strip "REQUIRED" / asterisks from label text. */
const cleanLabel = (text: string): string =>
  text.replace(/\s*(REQUIRED|required|\*)\s*/g, "").trim();

/**
 * Resolve a human-readable label for an input within a form.
 *
 * Priority:
 *   1. `data-validation-label` attribute on the input
 *   2. `<label for="inputId">` text content
 *   3. Nearest ancestor label's text content
 *   4. Falls back to the input's id or name
 */
const resolveLabel = (input: HTMLElement, form: HTMLElement): string => {
  const id = input.id || input.getAttribute("name") || "";

  const explicit = input.getAttribute("data-validation-label");
  if (explicit) return explicit;

  if (id) {
    const forLabel = form.querySelector<HTMLElement>(`label[for='${id}']`);
    if (forLabel) return cleanLabel(forLabel.textContent || id);
  }

  const ancestor = input.closest("label");
  if (ancestor) return cleanLabel(ancestor.textContent || id);

  return id;
};

/**
 * Resolve the best focusable target for an input.
 *
 * Handles:
 *   - Combobox rows: focus the text input, not the hidden validation input
 *   - CKEditor: provide a selector for the contenteditable div
 *   - Regular inputs: use the input itself
 */
const resolveFocusTarget = (
  input: HTMLElement,
): Pick<ErrorFieldEntry, "fieldId" | "selector"> => {
  const id = input.id || input.getAttribute("name") || "";

  // Filter combobox rows — focus the text input inside the row
  const filterRow = input.closest("[id^='filter-row-']");
  if (filterRow) {
    const comboboxInput = filterRow.querySelector<HTMLElement>(
      "input[role='combobox'], input[type='text']",
    );
    if (comboboxInput?.id) {
      return { fieldId: comboboxInput.id, selector: `#${comboboxInput.id}` };
    }
  }

  // CKEditor inline mode — CKEDITOR.inline() hides the original textarea and
  // creates a sibling div.cke_textarea_inline[contenteditable] next to it.
  // The toolbar wrapper (#cke_<id>) is a separate detached/floating element,
  // so the contenteditable is NOT inside it — target the sibling instead.
  if (input.getAttribute("data-rich-text") || input.classList.contains("rich_text")) {
    return {
      fieldId: id,
      selector: `#${id} ~ .cke_textarea_inline`,
    };
  }

  // Photo hidden input — focus the chooser element
  if (input.getAttribute("data-validation-focus")) {
    const focusId = input.getAttribute("data-validation-focus")!.replace("#", "");
    return { fieldId: focusId };
  }

  return { fieldId: id };
};

/**
 * Build a field registry by discovering all validated inputs within a
 * form element. Works with any form using `data-validation` attributes
 * and/or the HTML `required` attribute.
 *
 * @param form - The form element to scan
 * @returns Registry keyed by input id or name
 */
export const buildRegistryFromForm = (
  form: HTMLElement,
): Record<string, ErrorFieldEntry> => {
  const registry: Record<string, ErrorFieldEntry> = {};

  const selector = [
    "input[data-validation]",
    "input[required]",
    'input[data-required="true"]',
    'textarea[data-required="true"]',
    "textarea[data-validation]",
    "textarea[required]",
    "select[data-validation]",
    "select[required]",
  ].join(",");

  form.querySelectorAll<HTMLElement>(selector).forEach((input) => {
    const id = input.id || input.getAttribute("name") || "";
    if (!id) return;

    const label = resolveLabel(input, form);
    const focus = resolveFocusTarget(input);

    registry[id] = {
      label,
      ...focus,
    };
  });

  return registry;
};

/**
 * Focus the element matching the given error entry.
 * Handles hidden inputs (comboboxes), CKEditor contenteditable, and
 * normal inputs. Scrolls the target into view smoothly.
 */
export const focusField = (entry: ErrorFieldEntry): void => {
  requestAnimationFrame(() => {
    let element: HTMLElement | null = null;

    // Prefer the explicit selector when provided — the selector targets
    // the visible interactive element (e.g. CKEditor contenteditable,
    // combobox text input) while fieldId may point at a hidden original.
    if (entry.selector) {
      element = document.querySelector<HTMLElement>(entry.selector);
    }

    if (!element) {
      element = document.getElementById(entry.fieldId);
    }

    if (!element) return;

    // Hidden inputs can't receive focus — look for a visible sibling or parent input
    if (element instanceof HTMLInputElement && element.type === "hidden") {
      const parent = element.closest(".em-input_container, .em-input-floating, [id^='filter-row-']");
      const visible =
        parent?.querySelector<HTMLElement>(
          "input:not([type='hidden']), [contenteditable='true'], textarea, select",
        );
      if (visible) {
        element = visible;
      }
    }

    // CKEditor inline textarea fallback — when the selector didn't match,
    // the textarea is display:none but not type=hidden, so the hidden-input
    // check above doesn't catch it. Find the sibling editable div instead.
    if (
      element instanceof HTMLTextAreaElement &&
      (element.getAttribute("data-rich-text") || element.classList.contains("rich_text"))
    ) {
      const sibling = element.parentElement?.querySelector<HTMLElement>(
        ".cke_textarea_inline",
      );
      if (sibling) {
        element = sibling;
      }
    }

    // CKEditor wrapper — drill into the contenteditable
    if (element.classList.contains("cke")) {
      const editable = element.querySelector<HTMLElement>("[contenteditable='true']");
      if (editable) {
        element = editable;
      }
    }

    element.focus({ preventScroll: false });
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};
